import api from '../service/api.service';

import { ActiveSortField, DeductionTimeline, PaginationQueryParams } from '../../types';
import { parseQueryFilters } from '../../helpers/parseQueryFilters';
import { parseQuerySortFields } from '../../helpers/parseQuerySortFields';

const BASE_URL_GET_V1 = '/v1/deductions';
const BASE_URL_GET_V2 = '/v2/deductions';
const BASE_URL_V3 = '/v3/deductions';

export interface LienRecovery {
  id: number;
  recoveredAmount: number;
  recoveredDate: string;
  reason: string;
  note: string | null;
  documentLink: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface LienRecoveriesResponse {
  deductionId: number;
  deductionAmount: number;
  vendor: {
    id: number;
    name: string;
  } | null;
  totalRecovered: number;
  remainingRecoverable: number;
  // Newest recoveredDate first
  recoveries: LienRecovery[];
}

export interface CreateLienRecoveryRequest {
  recoveredAmount: number;
  recoveredDate: string;
  reason: string;
  note?: string;
}

// Send only the fields being changed. Pass FormData instead when uploading a new document.
export interface UpdateLienRecoveryRequest {
  recoveredAmount?: number;
  recoveredDate?: string;
  reason?: string;
  note?: string;
  removeDocument?: boolean;
}

export interface LienRecoveryDocument {
  downloadUrl: string;
  fileName: string;
}

export const getDeductionsList = async ({ size, page, count, match, filters, activeSortField }: PaginationQueryParams) => {
  const queryFilters = parseQueryFilters(filters);
  const sortFilters = parseQuerySortFields(activeSortField as ActiveSortField);

  const targetUrl = `${BASE_URL_GET_V2}?size=${size}&page=${page}&count=${count}&match=${match || ''}${
    queryFilters || ''
  }${sortFilters}`;

  return await api.get(targetUrl);
};

export const createDeductionRecord = async (data: unknown) => {
  // Check if data is FormData (for file uploads) and set appropriate headers
  const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
  return await api.post(BASE_URL_GET_V2, data, config);
};

export const getDeductionRecord = async (deductionId: string) => {
  const targetUrl = `${BASE_URL_GET_V2}/${deductionId}`;

  return await api.get(targetUrl);
};

export const updateDeductionRecord = async (deductionId: string, data: unknown) => {
  const targetUrl = `${BASE_URL_GET_V2}/${deductionId}`;
  const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};

  return await api.put(targetUrl, data, config);
};

export const getDeductionTimeline = async (deductionId: number) => {
  const targetUrl = `${BASE_URL_GET_V2}/${deductionId}/timeline`;

  return await api.get<DeductionTimeline[]>(targetUrl);
};

export const deleteDeductionRecord = async (deductionId: string) => {
  const targetUrl = `${BASE_URL_GET_V2}/${deductionId}`;
  return await api.delete(targetUrl);
};

export const getDocument = async (deductionId: string) => {
  const targetUrl = `${BASE_URL_GET_V1}/${deductionId}/document`;
  return await api.get(targetUrl);
};

export const deleteDocument = async (deductionId: string) => {
  const targetUrl = `${BASE_URL_GET_V1}/${deductionId}/document`;
  return await api.delete(targetUrl);
};

export const getRemittanceContextByDeduction = async (deductionId?: string) => {
  const id = Number(deductionId);
  const targetUrl = `${BASE_URL_GET_V1}/${id}/remittance-context`;
  return await api.get(targetUrl);
};

export const getRemittanceContextByCase = async (caseId?: number, firmId?: number) => {
  const targetUrl = `${BASE_URL_GET_V1}/remittance-context?caseId=${caseId}&firmId=${firmId}`;
  return await api.get(targetUrl);
};

export const getRemittanceList = async (deductionId: string) => {
  const targetUrl = `${BASE_URL_GET_V1}/${deductionId}/remittance-advice`;
  return await api.get(targetUrl);
};

export const createRemittanceRecord = async (deductionId: string, data: unknown) => {
  const targetUrl = `${BASE_URL_GET_V1}/${deductionId}/remittance-advice`;
  return await api.post(targetUrl, data);
};

export const updateRemittanceRecord = async (deductionId: string, lineId: string, data: unknown) => {
  const targetUrl = `${BASE_URL_GET_V1}/${deductionId}/remittance-advice/${lineId}`;
  return await api.put(targetUrl, data);
};

export const deleteRemittanceRecord = async (deductionId: string, lineId: string) => {
  const targetUrl = `${BASE_URL_GET_V1}/${deductionId}/remittance-advice/${lineId}`;
  return await api.delete(targetUrl);
};

export const getLienRecoveries = async (deductionId: number | string) => {
  const targetUrl = `${BASE_URL_V3}/${deductionId}/lien-recoveries`;
  return await api.get<LienRecoveriesResponse>(targetUrl);
};

export const createLienRecovery = async (
  deductionId: number | string,
  data: CreateLienRecoveryRequest | FormData,
) => {
  const targetUrl = `${BASE_URL_V3}/${deductionId}/lien-recoveries`;
  const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
  return await api.post<LienRecovery>(targetUrl, data, config);
};

export const updateLienRecovery = async (
  deductionId: number | string,
  recoveryId: number | string,
  data: UpdateLienRecoveryRequest | FormData,
) => {
  const targetUrl = `${BASE_URL_V3}/${deductionId}/lien-recoveries/${recoveryId}`;
  const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
  return await api.put<LienRecovery>(targetUrl, data, config);
};

export const deleteLienRecovery = async (deductionId: number | string, recoveryId: number | string) => {
  const targetUrl = `${BASE_URL_V3}/${deductionId}/lien-recoveries/${recoveryId}`;
  return await api.delete(targetUrl);
};

export const getLienRecoveryDocument = async (deductionId: number | string, recoveryId: number | string) => {
  const targetUrl = `${BASE_URL_V3}/${deductionId}/lien-recoveries/${recoveryId}/document`;
  return await api.get<LienRecoveryDocument>(targetUrl);
};
